import { enqueueSnackbar } from 'notistack';
import { ERROR_401 } from 'common/config/api';
import { SESSION_EXPIRED_MESSAGE, SESSION_EXPIRED_MESSAGE_TIMEOUT, SIGN_IN_VIEW } from 'common/constants/auth';
import {
  buildSignInUrl,
  singleFlightTokenRefresh,
} from 'pages/Auth/utils';
import BaseService from './base';

type AuthErrorLike = {
  status?: number;
};

const isAuthError = (error: unknown): error is AuthErrorLike =>
  typeof error === 'object' && error !== null && 'status' in error;

const handleExpiredSession = () => {
  enqueueSnackbar(SESSION_EXPIRED_MESSAGE, {
    variant: 'info',
  });
  // Prevent any further API requests
  setTimeout(() => {
    window.location.href = buildSignInUrl({
      returnTo: `${window.location.pathname}${window.location.search}`,
      view: SIGN_IN_VIEW.IDENTIFIER,
    });
  }, SESSION_EXPIRED_MESSAGE_TIMEOUT);
};

const refreshPassThrough = async <TResponse>(next: () => Promise<TResponse>): Promise<TResponse> => {
  try {
    return await next();
  } catch (error) {
    if (isAuthError(error) && error.status === Number(ERROR_401)) {
      const newAccess = await singleFlightTokenRefresh();

      if (newAccess) {
        return next();
      }

      // singleFlightTokenRefresh already cleared the session; surface the expiry UI.
      handleExpiredSession();
    }
    throw error;
  }
};

class BaseServiceRetryWrapper extends BaseService {
  async get<TResponse = unknown>(url: string, headers?: Record<string, string>): Promise<TResponse> {
    return refreshPassThrough(() => super.get<TResponse>(url, headers));
  }

  async getFile<TResponse = unknown, TPayload = unknown>(
    url: string,
    data: TPayload,
    headers?: Record<string, string>
  ): Promise<TResponse> {
    return refreshPassThrough(() => super.getFile<TResponse, TPayload>(url, data, headers));
  }

  async put<TResponse = unknown, TPayload = unknown>(
    url: string,
    data: TPayload,
    headers?: Record<string, string>
  ): Promise<TResponse> {
    return refreshPassThrough(() => super.put<TResponse, TPayload>(url, data, headers));
  }

  async post<TResponse = unknown, TPayload = unknown>(
    url: string,
    data?: TPayload,
    headers?: Record<string, string>
  ): Promise<TResponse> {
    return refreshPassThrough(() => super.post<TResponse, TPayload>(url, data, headers));
  }

  async delete<TResponse = unknown>(url: string, headers?: Record<string, string>): Promise<TResponse> {
    return refreshPassThrough(() => super.delete<TResponse>(url, headers));
  }

  async patch<TResponse = unknown, TPayload = unknown>(
    url: string,
    data: TPayload,
    headers?: Record<string, string>
  ): Promise<TResponse> {
    return refreshPassThrough(() => super.patch<TResponse, TPayload>(url, data, headers));
  }
}

export default BaseServiceRetryWrapper;
